// Sorteren van de Bill Shock-tabel.
//
// Twee regels die het scherm bruikbaar houden en die je niet met het oog kan
// nakijken op duizenden rijen, vandaar dat ze hier apart staan met een test:
//
//  1. De kolom "Klant" TOONT de naam volgens de platformregel (bedrijfsnaam bij
//     een zakelijke klant, contactpersoon bij een particulier). Sorteren gebeurt
//     op diezelfde waarde — sorteer je op het ruwe `customer`-veld, dan sorteert
//     de kolom op de contactpersoon terwijl er een bedrijfsnaam staat, en lijkt
//     de lijst willekeurig geordend.
//  2. Onder ELKE sortering geldt dezelfde tweede sleutel: klantnaam, en binnen
//     één klant de recentste berekening eerst. Zo staan de aansluitingen van
//     dezelfde klant altijd onder elkaar, ook wanneer je sorteert op een kolom
//     waarin veel records dezelfde waarde delen (segment, CP-status,
//     productgroep). Zonder die regel verspringen ze door elkaar en moet je de
//     rijen van één klant bij elkaar zoeken.
//
// `klantNaam` wordt meegegeven (customerPrimaryName uit App.jsx) zodat deze
// module niets van de order-vorm hoeft te weten.

// Sorteersleutels die geen kaal veld zijn.
const AFGELEIDE_SLEUTELS = {
  customer: (r, klantNaam) => klantNaam(r) || '',
};

// `extra` (optioneel) levert de sorteerwaarde voor kolommen die niet uit de rij
// zelf komen maar uit een berekening eromheen — zoals "Customer Bill Shock", het
// totaal van de klant waartoe deze rij behoort.
export function sortWaarde(row, sleutel, klantNaam, extra = null) {
  if (extra && typeof extra[sleutel] === 'function') return extra[sleutel](row);
  const afgeleid = AFGELEIDE_SLEUTELS[sleutel];
  return afgeleid ? afgeleid(row, klantNaam) : row[sleutel];
}

// Tweede sleutel: klantnaam A→Z, binnen de klant de recentste berekening eerst.
// Staat los van de gekozen richting: ook bij aflopend sorteren wil je de klanten
// gegroepeerd zien, niet omgekeerd door elkaar.
export function klantVolgorde(a, b, klantNaam) {
  const kv = String(klantNaam(a) || '').localeCompare(String(klantNaam(b) || ''), 'nl-BE');
  if (kv !== 0) return kv;
  return String(b.calculationUtc || '').localeCompare(String(a.calculationUtc || ''));
}

// Sorteert een kopie van `rows`. Lege waarden zakken altijd naar onderen, ook bij
// aflopend sorteren: een record zonder waarde is geen "hoogste" waarde.
export function sorteerBillShock(rows, sortConfig, klantNaam, extra = null) {
  const k = sortConfig?.key;
  const dir = sortConfig?.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = sortWaarde(a, k, klantNaam, extra), bv = sortWaarde(b, k, klantNaam, extra);
    if (av == null && bv == null) return klantVolgorde(a, b, klantNaam);
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      const d = (av - bv) * dir;
      return d !== 0 ? d : klantVolgorde(a, b, klantNaam);
    }
    const d = String(av).localeCompare(String(bv), 'nl-BE') * dir;
    return d !== 0 ? d : klantVolgorde(a, b, klantNaam);
  });
}

// Standaardsortering van de tabel: op klantnaam, zodat een klant met meerdere
// aansluitingen in één blok staat. Dat is hoe de pagina gebruikt wordt — één
// klant bekijken en bellen — en niet "wat is er als laatste doorgerekend".
export const BS_SORT_DEFAULT = { key: 'customer', dir: 'asc' };

// ── Top-selectie (Betalen / Ontvangen) ───────────────────────────────────────
//
// De Top toont niet langer losse records maar GROEPEN per klant: bovenaan het
// record dat de klant in de top bracht, daaronder al zijn andere berekeningen. Zo
// zie je meteen of een bijbetaling van € 800 op één EAN wordt goedgemaakt door
// een teruggave op een andere — met alleen het toprecord op het scherm trok je
// een verkeerde conclusie en belde je een klant die per saldo niets moet betalen.
//
// `niveau`:
//   'record' — rangschikken op de losste berekening (de klassieke top).
//   'klant'  — rangschikken op het NETTO van de klant: de som over al zijn
//              records. Een klant met tien kleine bijbetalingen komt dan wél
//              bovendrijven, en één uitschieter die intern wordt gecompenseerd
//              juist niet.
//
// In beide gevallen staat een klant HOOGSTENS ÉÉN keer in de lijst: twee records
// van dezelfde klant in de top zouden dezelfde groep twee keer tonen.

export const klantSleutel = (r, klantNaam) =>
  String(r.customerReference || klantNaam(r) || r.connectionPointGSRN || '').trim().toLowerCase();

const getal = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

// Netto van een groep records: som van de bill shocks, en de deviation daarvan
// tegenover de som van de settlements (niet het gemiddelde van de percentages —
// dat zou een berekening van € 5 even zwaar laten wegen als één van € 5.000).
export function nettoVanRecords(records) {
  const netto = records.reduce((s, r) => s + getal(r.billShockAmount), 0);
  const settlement = records.reduce((s, r) => s + getal(r.settlementAmount), 0);
  return { netto, settlement, deviation: settlement !== 0 ? (netto / settlement) * 100 : null };
}

// Het record dat de groep aanvoert: de grootste absolute bill shock. Dat is de
// berekening waar het gesprek met de klant over gaat.
const zwaarste = (records) =>
  [...records].sort((a, b) => Math.abs(getal(b.billShockAmount)) - Math.abs(getal(a.billShockAmount)))[0];

export function bouwTopGroepen(rijen, { richting, veld, niveau = 'record', minBedrag = 0, klantNaam }) {
  if (richting !== 'pay' && richting !== 'receive') return [];
  const positief = richting === 'pay';
  const perKlant = new Map();
  for (const r of rijen) {
    const k = klantSleutel(r, klantNaam);
    if (!perKlant.has(k)) perKlant.set(k, []);
    perKlant.get(k).push(r);
  }

  const groepen = [];
  if (niveau === 'klant') {
    for (const [sleutel, records] of perKlant) {
      const n = nettoVanRecords(records);
      const waarde = veld === 'billshockDeviationPercentage' ? n.deviation : n.netto;
      if (waarde == null || !Number.isFinite(waarde)) continue;
      if (positief ? waarde <= 0 : waarde >= 0) continue;
      if (Math.abs(n.netto) < minBedrag) continue;
      const lead = zwaarste(records);
      groepen.push({ sleutel, lead, overige: records.filter(r => r !== lead), ...n, rang: waarde });
    }
  } else {
    const gezien = new Set();
    const kandidaten = rijen.filter(r => {
      const v = Number(r[veld]);
      if (!Number.isFinite(v)) return false;
      if (positief ? v <= 0 : v >= 0) return false;
      return Math.abs(getal(r.billShockAmount)) >= minBedrag;
    }).sort((a, b) => positief ? Number(b[veld]) - Number(a[veld]) : Number(a[veld]) - Number(b[veld]));
    for (const lead of kandidaten) {
      const sleutel = klantSleutel(lead, klantNaam);
      if (gezien.has(sleutel)) continue;      // klant staat al in de lijst
      gezien.add(sleutel);
      const records = perKlant.get(sleutel) || [lead];
      groepen.push({ sleutel, lead, overige: records.filter(r => r !== lead), ...nettoVanRecords(records), rang: Number(lead[veld]) });
    }
  }
  // Zwaarste eerst: bij "betalen" het hoogste positieve, bij "ontvangen" het
  // laagste negatieve.
  return groepen.sort((a, b) => positief ? b.rang - a.rang : a.rang - b.rang);
}

// Groepen → platte rijen voor de tabel. `lead` markeert de gekleurde regel.
export function groepenNaarRijen(groepen, klantNaam) {
  const uit = [];
  for (const g of groepen) {
    uit.push({ r: g.lead, lead: true, groep: g });
    for (const r of [...g.overige].sort((a, b) => klantVolgorde(a, b, klantNaam))) {
      uit.push({ r, lead: false, groep: g });
    }
  }
  return uit;
}

// ── Weergave per klant ───────────────────────────────────────────────────────
//
// Eén regel per KLANT in plaats van per aansluiting: alle berekeningen binnen de
// actieve filters opgeteld. Beantwoordt dezelfde vraag als de netto-afsluiter in
// de Top, maar dan voor de hele lijst en dus ook exporteerbaar.
//
// Bedragen worden gesómd; de deviation wordt HERBEREKEND uit de sommen
// (netto ÷ settlement), niet gemiddeld — anders weegt een berekening van € 5
// even zwaar als één van € 5.000.
//
// Kenmerken die geen som kennen (energielabel, verkoopteam, verkoper, segment,
// productgroep, CP-status): alleen invullen als ÁLLE records van die klant dezelfde
// waarde dragen. Verschillen ze, dan blijft de cel leeg — willekeurig de eerste
// nemen zou een verkoper aanrekenen wat hij niet verkocht heeft, en "Meerdere" is
// geen waarde waarop je kan filteren of sorteren.
export const eensluidend = (records, veld) => {
  const set = new Set(records.map(r => String(r[veld] || '').trim()).filter(Boolean));
  return set.size === 1 ? [...set][0] : '';
};

export function aggregeerPerKlant(rijen, klantNaam) {
  const perKlant = new Map();
  for (const r of rijen) {
    const k = klantSleutel(r, klantNaam);
    if (!perKlant.has(k)) perKlant.set(k, []);
    perKlant.get(k).push(r);
  }
  const som = (records, veld) => records.reduce((s, r) => {
    const n = Number(r[veld]);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);
  return [...perKlant.entries()].map(([sleutel, records]) => {
    const { netto, settlement, deviation } = nettoVanRecords(records);
    const lead = [...records].sort((a, b) => Math.abs(Number(b.billShockAmount) || 0) - Math.abs(Number(a.billShockAmount) || 0))[0];
    return {
      // `id` en `billShockAmount` dragen dezelfde namen als een gewone rij, zodat
      // de kengetallen boven de tabel en de sortering ongewijzigd blijven werken.
      id: `klant-${sleutel}`,
      billShockAmount: netto,
      billshockDeviationPercentage: deviation,
      sleutel,
      klant: klantNaam(lead) || '',
      customerReference: lead?.customerReference || '',
      aantalBerekeningen: records.length,
      // Geslaagd/gefaald per klant, zodat de kengetallen boven de tabel in de
      // klantweergave dezelfde telling geven als in de EAN-weergave (zonder deze
      // twee telde de kop "0 succesvol, 0 gefaald" bij 3.000 aansluitingen).
      aantalSuccesvol: records.filter(r => r.isSuccess === true).length,
      aantalGefaald: records.filter(r => r.isSuccess === false).length,
      aantalAansluitingen: new Set(records.map(r => r.connectionPointGSRN).filter(Boolean)).size,
      laatsteBerekening: records.reduce((max, r) => (String(r.calculationUtc || '') > max ? String(r.calculationUtc || '') : max), ''),
      settlementAmount: settlement,
      proposedPrepaymentAmount: som(records, 'proposedPrepaymentAmount'),
      lastPrepaymentAmount: som(records, 'lastPrepaymentAmount'),
      estOfftakeKwh: som(records, 'estOfftakeKwh'),
      offtakeQuantityKWh: som(records, 'offtakeQuantityKWh'),
      estInjectionKwh: som(records, 'estInjectionKwh'),
      injectionQuantityKWh: som(records, 'injectionQuantityKWh'),
      company: eensluidend(records, 'company'),
      salespersonTeam: eensluidend(records, 'salespersonTeam'),
      salesperson: eensluidend(records, 'salesperson'),
      // Kenmerken van de aansluiting: enkel invullen als ze voor de hele klant
      // gelijk zijn. Zo blijft de kolom bruikbaar in plaats van misleidend.
      productGroep: eensluidend(records, 'productGroep'),
      connectionPointStatus: eensluidend(records, 'connectionPointStatus'),
      segment: eensluidend(records, 'segment'),
      meterMethod: eensluidend(records, 'meterMethod'),
      periodeMaanden: (() => {
        const set = new Set(records.map(r => r.periodeMaanden).filter(v => v != null));
        return set.size === 1 ? [...set][0] : null;
      })(),
      // Projectreferenties (bv. PROJP2Y/2026/07/560-13289) van de aansluitingen
      // van deze klant, ontdubbeld en gesorteerd. Anders dan de verkoopvelden
      // hierboven zijn meerdere waarden hier geen tegenstrijdigheid: een klant met
      // twee aansluitingen heeft gewoon twee referenties, en die horen allebei
      // in de export (kolom "Project ref").
      projectRefs: [...new Set(records.map(r => String(r.projectConnectionPoint?.projectReference || '').trim()).filter(Boolean))].sort(),
      records,
    };
  });
}

// Netto bill shock per klant over de MEEGEGEVEN set, als opzoektabel. Voedt de
// kolom "Customer Bill Shock" in de weergave per aansluiting: naast het bedrag van
// deze ene berekening staat dan wat de klant per saldo moet betalen of terugkrijgt.
// Draait op de gefilterde set, dus het totaal beschrijft altijd dezelfde selectie
// als de tabel eronder.
export function klantTotalen(rijen, klantNaam) {
  const totalen = new Map();
  for (const r of rijen) {
    const k = klantSleutel(r, klantNaam);
    const n = Number(r.billShockAmount);
    totalen.set(k, (totalen.get(k) || 0) + (Number.isFinite(n) ? n : 0));
  }
  return totalen;
}

// Vrije invoer van een bedrag uit het filterveld → getal, of null als er niets
// (bruikbaars) staat. Belgische notatie mag: "150,50" en "1.250,00"; een punt als
// decimaalteken ("150.50") ook, zolang er geen komma bij staat. Een euroteken of
// spaties storen niet. null = geen filter, dus "abc" zet de lijst niet leeg.
export function leesBedrag(tekst) {
  const s = String(tekst ?? '').replace(/[€\s]/g, '');
  if (!s) return null;
  const genormaliseerd = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  const n = Number(genormaliseerd);
  return Number.isFinite(n) ? n : null;
}

// Filter "Min. Customer Bill Shock": houd alleen de klanten over die per saldo
// MINSTENS `minimum` euro moeten bijbetalen — het netto over al hun aansluitingen
// binnen de meegegeven (al gefilterde) set. Het is een filter op de KLANT, niet op
// de losse berekening: valt een klant af, dan vallen al zijn rijen af, en wie
// blijft, blijft met al zijn rijen. Zo beschrijven de klanttotalen vóór en na de
// filter dezelfde selectie en toont "Per klant" dezelfde klanten als "Per
// aansluiting". Een klant die geld terugkrijgt heeft een negatief netto en valt
// bij elk minimum ≥ 0 weg. null = geen filter.
export function filterMinimumKlantBedrag(rijen, minimum, klantNaam) {
  if (minimum == null || !Number.isFinite(Number(minimum))) return rijen;
  const totalen = klantTotalen(rijen, klantNaam);
  return rijen.filter(r => (totalen.get(klantSleutel(r, klantNaam)) ?? 0) >= Number(minimum));
}

// Filter "Min. Customer Deviation (%)": zelfde mechaniek, maar op de afwijking
// per klant — netto bill shock gedeeld door de som van de afrekeningen, als
// percentage (precies wat de kolom "Deviation %" in de klantweergave toont, zie
// nettoVanRecords). Een klant zonder afrekeningsbedrag heeft geen afwijking en
// valt weg zodra de filter staat: "minstens x %" is voor hem niet waar te maken.
export function filterMinimumKlantDeviation(rijen, minimum, klantNaam) {
  if (minimum == null || !Number.isFinite(Number(minimum))) return rijen;
  const perKlant = new Map();
  for (const r of rijen) {
    const k = klantSleutel(r, klantNaam);
    if (!perKlant.has(k)) perKlant.set(k, []);
    perKlant.get(k).push(r);
  }
  const deviaties = new Map([...perKlant].map(([k, records]) => [k, nettoVanRecords(records).deviation]));
  return rijen.filter(r => {
    const d = deviaties.get(klantSleutel(r, klantNaam));
    return d != null && d >= Number(minimum);
  });
}
